import { useMemo, useState } from 'react';

import {
  Banknote,
  CircleAlert,
  BarChart3,
  Bell,
  Building,
  Calculator,
  Camera,
  Car,
  CheckCircle,
  ChevronDown,
  ClipboardList,
  Compass,
  CreditCard,
  DollarSign,
  FileSearch,
  FileSignature,
  FileText,
  Film,
  FolderTree,
  GraduationCap,
  HelpCircle,
  LayoutDashboard,
  LineChart,
  ListChecks,
  MapPin,
  Megaphone,
  Network,
  Package,
  PackageOpen,
  Percent,
  PieChart,
  PlusCircle,
  Repeat2,
  ReceiptText,
  RefreshCcw,
  Shield,
  ShoppingBag,
  Sliders,
  SlidersHorizontal,
  Ticket,
  Trash2,
  TrendingUp,
  Truck,
  UserCheck,
  UserCog,
  UserMinus,
  Users,
  Webhook,
  Workflow,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';

import { usePermissions } from '@alamo/auth';
import { SidebarFlyout, cn } from '@alamo/ui';
import type { SidebarFlyoutItem } from '@alamo/ui';

import { SidebarAggregateBadge } from './SidebarAggregateBadge';
import { SidebarAnalystQueueBadge } from './SidebarAnalystQueueBadge';
import { SidebarApprovalQueueBadge } from './SidebarApprovalQueueBadge';
import { SidebarApprovalsBadge } from './SidebarApprovalsBadge';
import { sumBadgeList, sumBadges } from './sidebarBadgeTree';
import type { SidebarBadgeCounts, SidebarBadgeKey } from './sidebarBadgeTree';
import { SidebarCommissionAttentionBadge } from './SidebarCommissionAttentionBadge';
import { SidebarCompetitorOfferBadge } from './SidebarCompetitorOfferBadge';
import type { SidebarCountBadgeVariant } from './SidebarCountBadge';
import { SidebarEndorsementQueueBadge } from './SidebarEndorsementQueueBadge';
import { SidebarPaymentIncidentBadge } from './SidebarPaymentIncidentBadge';
import { SidebarQuotationExceptionQueueBadge } from './SidebarQuotationExceptionQueueBadge';
import { SidebarReactivationBillingBadge } from './SidebarReactivationBillingBadge';
import { SidebarSupportBadge } from './SidebarSupportBadge';
import { SidebarVehicleQueueBadge } from './SidebarVehicleQueueBadge';
import { useSidebarBadgeCounts } from './useSidebarBadgeCounts';

interface SubItem {
  labelKey: string;
  to: string;
  icon?: LucideIcon;
  children?: SubItem[];
  permission?: { resource: string; action: string };
  /**
   * Nomeia a contagem que este item exibe. O número vem de
   * `useSidebarBadgeCounts()`; os ancestrais colapsados somam a contagem de toda
   * a descendência visível via `sumBadges`.
   */
  badgeKey?: SidebarBadgeKey;
  /**
   * Sobreescreve o matching padrão (`pathname === to` ou
   * `startsWith(`${to}/`)`) para rotas filhas que não compartilham prefixo
   * com `to` (ex.: `/admin/propostas/:id/analise` precisa ativar
   * `/admin/propostas/analise`).
   */
  activeWhen?: (pathname: string) => boolean;
}

interface AdminGroup {
  key: string;
  labelKey: string;
  icon: LucideIcon;
  items: SubItem[];
  /**
   * Permissão de portão do grupo — além de filtrar itens individuais, o grupo
   * inteiro fica invisível se o usuário não tiver esta permissão. Útil para
   * grupos onde todos os itens compartilham a mesma permissão e queremos evitar
   * que o grupo apareça com 0 itens por race-condition de permissões.
   */
  groupPermission?: { resource: string; action: string };
}

const SOURCE_GROUPS: AdminGroup[] = [
  {
    key: 'cadastros',
    labelKey: 'admin:sidebar.groups.cadastros',
    icon: Building,
    items: [
      {
        labelKey: 'admin:sidebar.items.parties',
        to: '/parties',
        icon: Users,
        permission: { resource: 'parties', action: 'read' },
      },
      {
        labelKey: 'admin:sidebar.items.users',
        to: '/users',
        icon: UserCog,
        permission: { resource: 'users', action: 'read' },
      },
      {
        labelKey: 'admin:sidebar.items.quotation_general_conditions',
        to: '/admin/condicoes-gerais-cotacao',
        icon: FileText,
        permission: { resource: 'quotation_general_conditions', action: 'manage' },
      },
      {
        labelKey: 'admin:sidebar.items.associate_contract_templates',
        to: '/admin/contratos/templates',
        icon: FileSignature,
        permission: { resource: 'contract_templates', action: 'manage' },
      },
      {
        labelKey: 'admin:sidebar.items.consultant_contract_templates',
        to: '/admin/contratos/templates-consultor',
        icon: FileSignature,
        permission: { resource: 'consultant_contract_templates', action: 'manage' },
      },
    ],
  },
  {
    key: 'cadastros_aprovacoes',
    labelKey: 'admin:sidebar.groups.cadastros_aprovacoes',
    icon: ClipboardList,
    items: [
      {
        labelKey: 'admin:sidebar.items.approval_queue',
        to: '/admin/registrations',
        icon: ClipboardList,
        permission: { resource: 'admin_registrations', action: 'read' },
        badgeKey: 'approvalQueue',
      },
      {
        labelKey: 'admin:sidebar.items.consultant_pre_registrations',
        to: '/admin/consultant-pre-registrations',
        icon: Users,
        permission: { resource: 'consultant_pre_registrations', action: 'read' },
      },
      // Phase 01 W5.3 — admin legal terms (2FA-protected promote)
      {
        labelKey: 'admin:sidebar.items.legal_terms',
        to: '/admin/legal-terms',
        icon: FileSignature,
        permission: { resource: 'legal_terms', action: 'read' },
      },
      // Phase 01 W3.9-polish — admin PF→PJ migration queue
      {
        labelKey: 'admin:sidebar.items.migrations',
        to: '/admin/migrations',
        icon: Repeat2,
        permission: { resource: 'admin_registrations', action: 'update' },
      },
    ],
  },
  {
    key: 'analise_propostas',
    labelKey: 'admin:sidebar.groups.analise_propostas',
    icon: FileSearch,
    items: [
      {
        labelKey: 'admin:sidebar.items.endorsements',
        to: '/admin/endossos',
        icon: FileText,
        permission: { resource: 'policies', action: 'admin' },
        badgeKey: 'endorsementQueue',
        activeWhen: (pathname) =>
          pathname === '/admin/endossos' || pathname.startsWith('/admin/endossos/'),
      },
      {
        labelKey: 'admin:sidebar.items.analyst_queue',
        to: '/admin/propostas/analise',
        icon: ClipboardList,
        permission: { resource: 'proposals', action: 'analysis_read' },
        badgeKey: 'analystQueue',
        // Ativar também para as rotas irmãs de detalhe da análise.
        activeWhen: (pathname) =>
          pathname === '/admin/propostas/analise' ||
          /^\/admin\/propostas\/[^/]+\/analise(?:-nova)?$/.test(pathname),
      },
      {
        labelKey: 'admin:sidebar.items.quotation_exceptions',
        to: '/admin/quotation-exceptions',
        icon: CircleAlert,
        permission: { resource: 'quotation_exception', action: 'read_all' },
        badgeKey: 'quotationExceptionQueue',
        activeWhen: (pathname) =>
          pathname === '/admin/quotation-exceptions' ||
          pathname.startsWith('/admin/quotation-exceptions/') ||
          pathname === '/admin/provisional-quotations' ||
          pathname.startsWith('/admin/provisional-quotations/'),
      },
      {
        labelKey: 'admin:sidebar.items.kpis',
        to: '/admin/kpis',
        icon: LineChart,
        permission: { resource: 'kpis', action: 'read' },
      },
      {
        labelKey: 'admin:sidebar.items.analyst_team',
        to: '/admin/analistas',
        icon: UserCheck,
        permission: { resource: 'analyst_team', action: 'manage' },
      },
      {
        labelKey: 'admin:sidebar.items.rejection_reasons',
        to: '/admin/motivos-recusa',
        icon: ListChecks,
        permission: { resource: 'rejection_reasons', action: 'manage' },
      },
      {
        labelKey: 'admin:sidebar.items.sga_integration',
        to: '/admin/policies',
        icon: Workflow,
        // `policies:read` é da carteira do consultor — o back-office exige a
        // permission dedicada `policies:admin` (admin_alamo + gerente).
        permission: { resource: 'policies', action: 'admin' },
      },
    ],
  },
  {
    key: 'vistoria_documentos',
    labelKey: 'admin:sidebar.groups.vistoria_documentos',
    icon: Camera,
    items: [
      {
        labelKey: 'admin:sidebar.items.inspection_types',
        to: '/admin/vistoria/tipos',
        icon: Camera,
        permission: { resource: 'inspection_types', action: 'manage' },
      },
      {
        labelKey: 'admin:sidebar.items.document_types',
        to: '/admin/documentos/tipos',
        icon: FileText,
        permission: { resource: 'document_types', action: 'manage' },
      },
    ],
  },
  // Phase 01 W5.2 / W5.6 / Phase 02 — "Aprendizado" e métricas.
  {
    key: 'aprendizado',
    labelKey: 'admin:sidebar.groups.aprendizado',
    icon: GraduationCap,
    items: [
      {
        labelKey: 'admin:sidebar.items.trainings',
        to: '/admin/trainings',
        icon: GraduationCap,
        permission: { resource: 'training', action: 'manage' },
      },
      {
        labelKey: 'admin:sidebar.items.video_library',
        to: '/admin/video-library',
        icon: Film,
        permission: { resource: 'video_library', action: 'read' },
      },
      {
        labelKey: 'admin:sidebar.items.onboarding_metrics',
        to: '/admin/dashboard/onboarding',
        icon: LineChart,
        permission: { resource: 'analytics', action: 'read' },
      },
      {
        labelKey: 'admin:sidebar.items.tour_settings',
        to: '/admin/settings/tour',
        icon: Compass,
        permission: { resource: 'settings', action: 'manage' },
      },
    ],
  },
  // Indicadores operacionais e relatórios gerenciais. Cada tela tem permission própria
  // (`reports:*`), então NÃO há groupPermission: o gate de grupo é um AND e
  // esconderia o grupo inteiro de quem recebeu só um relatório. Grupo sem item
  // visível já é escondido pelo filtro de `items.length > 0`.
  // Contrato: docs/contracts/ESCR-relatorios-gerenciais.md.
  {
    key: 'relatorios',
    labelKey: 'reports:menu.group',
    icon: BarChart3,
    items: [
      {
        labelKey: 'reports:menu.indicators',
        to: '/relatorios/indicadores',
        icon: BarChart3,
        permission: { resource: 'reports', action: 'indicators' },
      },
      {
        labelKey: 'reports:menu.analyst_productivity',
        to: '/relatorios/produtividade-analistas',
        icon: UserCheck,
        permission: { resource: 'reports', action: 'analyst_productivity' },
      },
      {
        labelKey: 'reports:menu.vehicle_ranking',
        to: '/relatorios/ranking-veiculos',
        icon: Car,
        permission: { resource: 'reports', action: 'vehicle_ranking' },
      },
      {
        labelKey: 'reports:menu.regional_conversion',
        to: '/relatorios/conversao-regiao',
        icon: MapPin,
        permission: { resource: 'reports', action: 'regional_conversion' },
      },
      {
        labelKey: 'reports:menu.monthly_closing',
        to: '/relatorios/fechamento-mes',
        icon: LineChart,
        permission: { resource: 'reports', action: 'monthly_closing' },
      },
      {
        labelKey: 'reports:menu.price_elasticity',
        to: '/relatorios/elasticidade-preco',
        icon: TrendingUp,
        permission: { resource: 'reports', action: 'price_elasticity' },
      },
      {
        labelKey: 'reports:menu.portfolio_concentration',
        to: '/relatorios/concentracao-carteira',
        icon: PieChart,
        permission: { resource: 'reports', action: 'concentration' },
      },
      {
        labelKey: 'reports:menu.retention',
        to: '/relatorios/retencao',
        icon: Repeat2,
        permission: { resource: 'reports', action: 'retention' },
      },
      {
        labelKey: 'reports:menu.reactivations',
        to: '/relatorios/reativacoes',
        icon: RefreshCcw,
        permission: { resource: 'reports', action: 'reactivations' },
      },
    ],
  },
  {
    key: 'rede',
    labelKey: 'admin:sidebar.groups.rede',
    icon: Network,
    items: [
      {
        labelKey: 'admin:sidebar.items.network',
        to: '/admin/network',
        icon: Network,
        permission: { resource: 'network_dashboard', action: 'read' },
      },
      {
        labelKey: 'admin:sidebar.items.network_consultants',
        to: '/admin/network/consultants',
        icon: Users,
        permission: { resource: 'network_consultants', action: 'read' },
        // Mantém "Consultores" ativo na lista + detalhe (/:id) + cadastro (/new),
        // mas NÃO na rota-irmã de inatividade (que tem item próprio).
        activeWhen: (pathname) =>
          pathname === '/admin/network/consultants' ||
          (pathname.startsWith('/admin/network/consultants/') &&
            !pathname.startsWith('/admin/network/consultants/inatividade')),
      },
      {
        labelKey: 'admin:sidebar.items.consultant_inactivity',
        to: '/admin/network/consultants/inatividade',
        icon: UserMinus,
        permission: { resource: 'consultant_inactivity', action: 'read' },
      },
      {
        labelKey: 'admin:sidebar.items.portfolio_transfer',
        to: '/admin/consultores/transferencia-carteira',
        icon: Repeat2,
        permission: { resource: 'consultant_portfolio', action: 'read' },
      },
      // Phase 01 W5.4 — admin lead distribution rules
      {
        labelKey: 'admin:sidebar.items.lead_distribution',
        to: '/admin/lead-distribution',
        icon: Workflow,
        permission: { resource: 'admin_network', action: 'manage' },
      },
      {
        labelKey: 'admin:retention_pool.menu',
        to: '/admin/retention/lead-pool',
        icon: Users,
        permission: { resource: 'lead_distribution', action: 'manage' },
      },
    ],
  },
  {
    // As rotas /admin/pricing/* concentram os 4 pilares + geografia + simulador
    // + dashboard. Vehicle Catalog é superfície separada.
    // /admin/pricing-tables redireciona para /admin/pricing.
    key: 'pricing',
    labelKey: 'admin:sidebar.groups.pricing',
    icon: Car,
    items: [
      {
        labelKey: 'admin:sidebar.items.vehicle_catalog',
        to: '/admin/vehicle-catalog',
        icon: Car,
        permission: { resource: 'vehicle-catalog', action: 'read' },
        badgeKey: 'vehicleQueue',
      },
      {
        labelKey: 'admin_pricing:sidebar.dashboard',
        to: '/admin/pricing',
        icon: LayoutDashboard,
        permission: { resource: 'pricing', action: 'manage' },
      },
      {
        // ESCR-205.1/.2 (F2) — fila central de aprovações.
        labelKey: 'admin_pricing:sidebar.approvals',
        to: '/admin/pricing/approvals',
        icon: CheckCircle,
        permission: { resource: 'pricing', action: 'manage' },
        badgeKey: 'approvals',
      },
      {
        labelKey: 'admin_pricing:sidebar.pilar_1',
        to: '/admin/pricing/pilar-1-tabela-base',
        icon: Sliders,
        permission: { resource: 'pricing', action: 'manage' },
      },
      {
        labelKey: 'admin_pricing:sidebar.pilar_2',
        to: '/admin/pricing/pilar-2-produtos',
        icon: Package,
        permission: { resource: 'pricing', action: 'manage' },
      },
      // ESCR-207.14 — entrada "Matriz Comparativa" removida da sidebar.
      // Matriz agora é tab interna de /admin/pricing/pilar-2-produtos?tab=matriz
      // (acessível pelo link da Pilar 2). Sidebar fica com 1 entrada por pilar.
      {
        labelKey: 'admin_pricing:sidebar.pilar_3',
        to: '/admin/pricing/pilar-3-ajustes',
        icon: SlidersHorizontal,
        permission: { resource: 'pricing', action: 'manage' },
      },
      {
        labelKey: 'admin_pricing:sidebar.pilar_4',
        to: '/admin/pricing/pilar-4-franquias-ativacao',
        icon: Shield,
        permission: { resource: 'pricing', action: 'manage' },
      },
      {
        labelKey: 'admin_pricing:sidebar.protected_value_rules',
        to: '/admin/pricing/protected-value-rules',
        icon: Percent,
        permission: { resource: 'pricing', action: 'manage' },
      },
      {
        labelKey: 'admin_pricing:sidebar.geografia',
        to: '/admin/pricing/geografia',
        icon: MapPin,
        permission: { resource: 'pricing', action: 'manage' },
      },
      {
        labelKey: 'admin_pricing:sidebar.quotation_simulator',
        to: '/admin/pricing/quotation-simulator',
        icon: Calculator,
        permission: { resource: 'pricing', action: 'manage' },
      },
    ],
  },
  {
    // Coverage Map dashboard — visão analítica (cobertura por consultor +
    // cotações). Independente de Pricing.
    key: 'validacoes_cobertura',
    labelKey: 'admin:sidebar.groups.validacoes_cobertura',
    icon: MapPin,
    items: [
      // ESCR-172.6 — Álamo Cobre: fila administrativa de cobertura de oferta.
      {
        labelKey: 'competitor_offer:admin.sidebar.item',
        to: '/admin/competitor-offers',
        icon: Shield,
        permission: { resource: 'competitor-offers', action: 'view-queue' },
        badgeKey: 'competitorOfferQueue',
      },
      {
        labelKey: 'admin:sidebar.items.coverage_map',
        to: '/admin/coverage-map',
        icon: LayoutDashboard,
        permission: { resource: 'coverage_map', action: 'read' },
      },
      {
        labelKey: 'admin:sidebar.items.suggestions',
        to: '/admin/suggestions',
        icon: ListChecks,
        permission: { resource: 'suggestion', action: 'read' },
      },
    ],
  },
  {
    // Marketing — campanhas/cupons promocionais (ESCR-185) + comunicados (ESCR-269).
    key: 'marketing',
    labelKey: 'admin:sidebar.groups.marketing',
    icon: Megaphone,
    items: [
      {
        labelKey: 'admin:sidebar.items.campaigns',
        to: '/admin/marketing/campanhas',
        icon: Megaphone,
        permission: { resource: 'campaigns', action: 'read' },
      },
      {
        labelKey: 'admin:sidebar.items.comunicados',
        to: '/admin/comunicados',
        icon: Megaphone,
        permission: { resource: 'announcements', action: 'manage' },
      },
    ],
  },
  {
    key: 'mensageria',
    labelKey: 'admin:sidebar.groups.mensageria',
    icon: Bell,
    items: [
      {
        labelKey: 'admin:sidebar.items.messaging_overview',
        to: '/admin/notifications',
        icon: LayoutDashboard,
        permission: { resource: 'notification_settings', action: 'read' },
        activeWhen: (pathname) => pathname === '/admin/notifications',
      },
      {
        labelKey: 'admin:sidebar.items.messaging_automatic',
        to: '/admin/notifications/events',
        icon: Bell,
        permission: { resource: 'notification_settings', action: 'manage' },
        activeWhen: (pathname) =>
          [
            '/admin/notifications/events',
            '/admin/notifications/templates',
            '/admin/notifications/cadence',
          ].some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)),
      },
      {
        labelKey: 'admin:sidebar.items.messaging_operation',
        to: '/admin/notifications/providers',
        icon: Workflow,
        permission: { resource: 'notification_operations', action: 'read' },
        activeWhen: (pathname) =>
          [
            '/admin/notifications/providers',
            '/admin/notifications/deliveries',
            '/admin/webhooks',
          ].some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)),
      },
      {
        labelKey: 'admin:sidebar.items.marketing_rd',
        to: '/admin/notifications/marketing-rd',
        icon: Megaphone,
        permission: { resource: 'marketing_integrations', action: 'read' },
      },
      {
        labelKey: 'admin:sidebar.items.integration_webhooks',
        to: '/admin/notifications/integration-webhooks',
        icon: Webhook,
        permission: { resource: 'integration_webhooks', action: 'manage' },
      },
      {
        labelKey: 'admin:sidebar.items.messaging_system',
        to: '/admin/notifications/system',
        icon: Shield,
        permission: { resource: 'notification_templates', action: 'read' },
      },
    ],
  },
  {
    key: 'configuracao',
    labelKey: 'admin:sidebar.groups.configuracao',
    icon: Sliders,
    items: [
      {
        labelKey: 'admin:sidebar.items.roles',
        to: '/permissions',
        icon: Shield,
        permission: { resource: 'permissions', action: 'read' },
      },
      {
        // Tela genérica de configurações do sistema (registry-driven).
        // activeWhen exato: /admin/settings/tour e /admin/settings/payment-gateway
        // têm itens próprios em outros grupos e não devem acender este.
        labelKey: 'admin:sidebar.items.system_settings',
        to: '/admin/settings',
        icon: SlidersHorizontal,
        permission: { resource: 'settings', action: 'read' },
        activeWhen: (pathname) => pathname === '/admin/settings',
      },
      {
        // Rubrica da Álamo (imagem nos PDFs) + contra-assinatura eletrônica.
        labelKey: 'admin:sidebar.items.signature_settings',
        to: '/admin/settings/assinatura',
        icon: FileSignature,
        permission: { resource: 'signature_settings', action: 'read' },
      },
    ],
  },
  {
    key: 'privacidade',
    labelKey: 'admin:sidebar.groups.privacidade',
    icon: Shield,
    items: [
      {
        labelKey: 'admin:sidebar.items.audit',
        to: '/audit',
        icon: FileSearch,
        permission: { resource: 'audit', action: 'read' },
      },
      {
        labelKey: 'admin:sidebar.items.deletions',
        to: '/admin/privacy/deletion-requests',
        icon: Trash2,
        permission: { resource: 'privacy', action: 'manage_deletions' },
      },
    ],
  },
  // Pagamentos — gateway de cobrança ativo (Asaas | Iugu).
  {
    key: 'pagamentos',
    labelKey: 'admin:sidebar.groups.pagamentos',
    icon: CreditCard,
    items: [
      {
        labelKey: 'admin:sidebar.items.payment_gateway',
        to: '/admin/settings/payment-gateway',
        icon: CreditCard,
        permission: { resource: 'payment_gateway', action: 'manage' },
      },
      {
        labelKey: 'admin:sidebar.items.activation_payments_report',
        to: '/admin/activation-payments',
        icon: ReceiptText,
        permission: { resource: 'activation_payments_report', action: 'read' },
      },
      {
        labelKey: 'admin:sidebar.items.activation_fee_report',
        to: '/admin/activation-fees-by-consultant',
        icon: BarChart3,
        permission: { resource: 'activation_fee_report', action: 'read' },
      },
      // Repasse manual da adesão (pending_payouts) — Iugu único / split gated.
      {
        labelKey: 'admin:sidebar.items.pending_payouts',
        to: '/admin/pending-payouts',
        icon: Banknote,
        permission: { resource: 'payouts', action: 'read' },
      },
      {
        labelKey: 'admin:sidebar.items.payment_incidents',
        to: '/admin/payment-incidents',
        icon: CircleAlert,
        permission: { resource: 'payment_incidents', action: 'read' },
        badgeKey: 'paymentIncidentOpen',
      },
      {
        labelKey: 'admin:sidebar.items.reactivation_billing',
        to: '/admin/reactivation-billing',
        icon: ReceiptText,
        permission: { resource: 'reactivation_billing', action: 'read' },
        badgeKey: 'reactivationBillingOpen',
      },
      {
        labelKey: 'admin:sidebar.items.payment_onboarding_operations',
        to: '/admin/payment-onboarding',
        icon: Workflow,
        permission: { resource: 'payment_onboarding', action: 'manage' },
      },
    ],
  },
  // Comissão MLM Genial — cadastro de patamares + fechamento + extrato.
  {
    key: 'comissoes',
    labelKey: 'commissions:menu.group',
    icon: DollarSign,
    items: [
      {
        labelKey: 'commissions:menu.tiers',
        to: '/admin/comissoes/patamares',
        icon: SlidersHorizontal,
        permission: { resource: 'commissions', action: 'configure' },
      },
      {
        labelKey: 'commissions:menu.closing',
        to: '/admin/comissoes/fechamento',
        icon: Calculator,
        permission: { resource: 'commissions', action: 'process' },
      },
      {
        labelKey: 'commissions:menu.extract',
        to: '/admin/comissoes/extrato',
        icon: FileText,
        permission: { resource: 'commissions', action: 'view_statement' },
        badgeKey: 'commissionAttention',
      },
      // Ajustes manuais (crédito/débito). Item visível com commissions:manage
      // (leitura); as ações de mutação da página exigem commissions:adjust.
      {
        labelKey: 'commissions:menu.adjustments',
        to: '/admin/comissoes/lancamentos',
        icon: PlusCircle,
        permission: { resource: 'commissions', action: 'adjust' },
      },
    ],
  },
  // Phase 34 Plan 07 — Loja Interna admin back-office.
  // Cada item tem permission própria de uma sub-área (`ecommerce:catalog`,
  // `:stock`, `:vouchers`, `:orders`, `:credit`, `:installments`,
  // `:financial_report`), então NÃO há groupPermission: o gate de grupo é um
  // AND e esconderia o submenu inteiro de quem recebeu só uma sub-área (um
  // perfil financeiro com `:credit` + `:installments` perdia o menu por não
  // ter `:catalog`). Consultor não vaza: ele tem só `ecommerce:read`, que não
  // gateia item nenhum daqui, e o pai só aparece com ao menos um filho visível.
  {
    key: 'loja',
    labelKey: 'admin:sidebar.groups.loja',
    icon: ShoppingBag,
    items: [
      {
        labelKey: 'admin:sidebar.items.store_products',
        to: '/admin/store/products',
        icon: Package,
        permission: { resource: 'ecommerce', action: 'catalog' },
      },
      {
        labelKey: 'admin:sidebar.items.store_stock',
        to: '/admin/store/stock',
        icon: PackageOpen,
        permission: { resource: 'ecommerce', action: 'stock' },
      },
      {
        labelKey: 'admin:sidebar.items.store_vouchers',
        to: '/admin/store/vouchers',
        icon: Ticket,
        permission: { resource: 'ecommerce', action: 'vouchers' },
      },
      {
        labelKey: 'admin:sidebar.items.store_orders',
        to: '/admin/store/orders',
        icon: ShoppingBag,
        permission: { resource: 'ecommerce', action: 'orders' },
      },
      {
        labelKey: 'admin:sidebar.items.store_credit',
        to: '/admin/store/credit',
        icon: CreditCard,
        permission: { resource: 'ecommerce', action: 'credit' },
      },
      {
        labelKey: 'admin:sidebar.items.store_installments',
        to: '/admin/store/credit/installments',
        icon: ListChecks,
        permission: { resource: 'ecommerce', action: 'installments' },
      },
      {
        labelKey: 'admin:sidebar.items.store_financial',
        to: '/admin/store/credit/financial',
        icon: LineChart,
        permission: { resource: 'ecommerce', action: 'financial_report' },
      },
    ],
  },
  // ESCR-EPIC-35 — Fornecedores externos (rastreamento + assistência).
  // Ver a fila e os cadastros exige `suppliers:read`; operar o dispatch é
  // `suppliers:dispatch`, cobrado dentro da própria fila.
  {
    key: 'fornecedores',
    labelKey: 'admin:sidebar.groups.fornecedores',
    icon: Truck,
    groupPermission: { resource: 'suppliers', action: 'read' },
    items: [
      {
        labelKey: 'admin:sidebar.items.suppliers',
        to: '/admin/suppliers',
        icon: Truck,
        permission: { resource: 'suppliers', action: 'read' },
        // /admin/suppliers + detalhe (/:id) ativam "Fornecedores"; as rotas-irmãs
        // de dispatches/regras têm item próprio.
        activeWhen: (pathname) => pathname.startsWith('/admin/suppliers'),
      },
      {
        labelKey: 'admin:sidebar.items.tracking_dispatches',
        to: '/admin/tracking/dispatches',
        icon: ListChecks,
        permission: { resource: 'tracking_dispatches', action: 'read' },
      },
      {
        labelKey: 'admin:sidebar.items.fulfillment_rules',
        to: '/admin/fulfillment-rules',
        icon: SlidersHorizontal,
        permission: { resource: 'fulfillment_rules', action: 'read' },
      },
    ],
  },
  // Central de Suporte / FAQ — CRUD de perguntas + categorias (admin_alamo).
  {
    key: 'faq',
    labelKey: 'admin:sidebar.groups.faq',
    icon: HelpCircle,
    groupPermission: { resource: 'faq', action: 'manage' },
    items: [
      {
        labelKey: 'admin:sidebar.items.faq_entries',
        to: '/admin/faq',
        icon: HelpCircle,
        permission: { resource: 'faq', action: 'manage' },
        // /admin/faq + /admin/faq/new + /admin/faq/:id/edit ativam "Perguntas";
        // /admin/faq/categories tem seu próprio item.
        activeWhen: (pathname) =>
          pathname === '/admin/faq' ||
          (pathname.startsWith('/admin/faq/') && !pathname.startsWith('/admin/faq/categories')),
      },
      {
        labelKey: 'admin:sidebar.items.faq_categories',
        to: '/admin/faq/categories',
        icon: FolderTree,
        permission: { resource: 'faq_categories', action: 'manage' },
      },
    ],
  },
  // ESCR-271.3 — Central de Chamados: fila do atendente. Distinto do FAQ.
  // groupPermission garante que quem não tem `support:manage` (gerente,
  // consultores) nunca veja o grupo.
  {
    key: 'suporte',
    labelKey: 'support:tickets.admin.menu_group',
    icon: Ticket,
    groupPermission: { resource: 'support', action: 'manage' },
    items: [
      {
        labelKey: 'support:tickets.admin.menu_item',
        to: '/admin/support',
        icon: Ticket,
        permission: { resource: 'support', action: 'manage' },
        badgeKey: 'support',
      },
    ],
  },
];

const sourceItems = (key: string): SubItem[] =>
  SOURCE_GROUPS.find((group) => group.key === key)?.items ?? [];

const createSourceSubmenu = (
  labelKey: string,
  to: string,
  icon: LucideIcon,
  sourceKey: string,
): SubItem => {
  const sourceGroup = SOURCE_GROUPS.find((group) => group.key === sourceKey);
  return {
    labelKey,
    to,
    icon,
    children: sourceGroup?.items ?? [],
    permission: sourceGroup?.groupPermission,
  };
};

const REPORTS_SUBMENU = createSourceSubmenu(
  'reports:menu.group',
  '/relatorios/produtividade-analistas',
  BarChart3,
  'relatorios',
);

const PRICING_ITEMS = sourceItems('pricing');
const VEHICLE_CATALOG_ITEM = PRICING_ITEMS.find((item) => item.to === '/admin/vehicle-catalog');
const PRICING_SUBMENU: SubItem = {
  labelKey: 'admin:sidebar.groups.precificacao',
  to: '/admin/pricing',
  icon: Calculator,
  children: PRICING_ITEMS.filter((item) => item.to !== '/admin/vehicle-catalog'),
};

const ADMIN_GROUP_DEFINITIONS: Array<{
  key: string;
  labelKey: string;
  icon: LucideIcon;
  sourceKeys: string[];
  additionalItems?: SubItem[];
}> = [
  {
    key: 'operacao',
    labelKey: 'admin:sidebar.groups.operacao',
    icon: ClipboardList,
    sourceKeys: [],
    additionalItems: [
      createSourceSubmenu(
        'admin:sidebar.groups.cadastros_aprovacoes',
        '/admin/registrations',
        ClipboardList,
        'cadastros_aprovacoes',
      ),
      createSourceSubmenu(
        'admin:sidebar.groups.analise_propostas',
        '/admin/propostas/analise',
        FileSearch,
        'analise_propostas',
      ),
      createSourceSubmenu(
        'admin:sidebar.groups.validacoes_cobertura',
        '/admin/competitor-offers',
        Shield,
        'validacoes_cobertura',
      ),
      createSourceSubmenu(
        'admin:sidebar.groups.vistoria_documentos',
        '/admin/vistoria/tipos',
        Camera,
        'vistoria_documentos',
      ),
      REPORTS_SUBMENU,
    ],
  },
  {
    key: 'pessoas_rede',
    labelKey: 'admin:sidebar.groups.pessoas_rede',
    icon: Users,
    sourceKeys: [],
    additionalItems: [
      createSourceSubmenu('admin:sidebar.groups.cadastros', '/users', Building, 'cadastros'),
      createSourceSubmenu('admin:sidebar.groups.rede', '/admin/network', Network, 'rede'),
      createSourceSubmenu(
        'admin:sidebar.groups.aprendizado',
        '/admin/trainings',
        GraduationCap,
        'aprendizado',
      ),
    ],
  },
  {
    key: 'produtos_precos',
    labelKey: 'admin:sidebar.groups.produtos_precos',
    icon: Car,
    sourceKeys: [],
    additionalItems: [...(VEHICLE_CATALOG_ITEM ? [VEHICLE_CATALOG_ITEM] : []), PRICING_SUBMENU],
  },
  {
    key: 'financeiro',
    labelKey: 'admin:sidebar.groups.financeiro',
    icon: CreditCard,
    sourceKeys: [],
    additionalItems: [
      createSourceSubmenu(
        'admin:sidebar.groups.pagamentos',
        '/admin/settings/payment-gateway',
        CreditCard,
        'pagamentos',
      ),
      createSourceSubmenu(
        'commissions:menu.group',
        '/admin/comissoes/patamares',
        DollarSign,
        'comissoes',
      ),
      createSourceSubmenu(
        'admin:sidebar.groups.loja',
        '/admin/store/products',
        ShoppingBag,
        'loja',
      ),
    ],
  },
  {
    key: 'comunicacao',
    labelKey: 'admin:sidebar.groups.comunicacao',
    icon: Megaphone,
    sourceKeys: [],
    additionalItems: [
      createSourceSubmenu(
        'admin:sidebar.groups.marketing',
        '/admin/marketing/campanhas',
        Megaphone,
        'marketing',
      ),
    ],
  },
  {
    key: 'mensageria',
    labelKey: 'admin:sidebar.groups.mensageria',
    icon: Bell,
    sourceKeys: ['mensageria'],
  },
  {
    key: 'governanca',
    labelKey: 'admin:sidebar.groups.governanca',
    icon: Shield,
    sourceKeys: [],
    additionalItems: [
      createSourceSubmenu(
        'admin:sidebar.groups.configuracao',
        '/permissions',
        Sliders,
        'configuracao',
      ),
      createSourceSubmenu('admin:sidebar.groups.privacidade', '/audit', Shield, 'privacidade'),
    ],
  },
  {
    key: 'servicos',
    labelKey: 'admin:sidebar.groups.servicos',
    icon: HelpCircle,
    sourceKeys: [],
    additionalItems: [
      createSourceSubmenu(
        'admin:sidebar.groups.fornecedores',
        '/admin/suppliers',
        Truck,
        'fornecedores',
      ),
      createSourceSubmenu('admin:sidebar.groups.faq', '/admin/faq', HelpCircle, 'faq'),
      createSourceSubmenu('support:tickets.admin.menu_group', '/admin/support', Ticket, 'suporte'),
    ],
  },
];

const GROUPS: AdminGroup[] = ADMIN_GROUP_DEFINITIONS.map((definition) => ({
  key: definition.key,
  labelKey: definition.labelKey,
  icon: definition.icon,
  items: [
    ...definition.sourceKeys.flatMap(
      (sourceKey) => SOURCE_GROUPS.find((group) => group.key === sourceKey)?.items ?? [],
    ),
    ...(definition.additionalItems ?? []),
  ],
}));

/**
 * Active matching para um item: respeita `activeWhen` quando definido,
 * senão usa o matching default (prefix + exact-only quando outro item
 * do mesmo group começa com `${to}/`).
 */
const isItemActive = (item: SubItem, pathname: string, siblings: SubItem[]): boolean => {
  if (item.children?.length) {
    return item.children.some((child) => isItemActive(child, pathname, item.children ?? []));
  }
  if (item.activeWhen) return item.activeWhen(pathname);
  const needsExactMatch = siblings.some(
    (other) => other.to !== item.to && other.to.startsWith(`${item.to}/`),
  );
  return needsExactMatch
    ? pathname === item.to
    : pathname === item.to || pathname.startsWith(`${item.to}/`);
};

const findActiveGroupKey = (pathname: string): string | undefined =>
  GROUPS.find((g) => g.items.some((i) => isItemActive(i, pathname, g.items)))?.key;

const filterVisibleItems = (
  items: SubItem[],
  hasPermission: (resource: string, action: string) => boolean,
): SubItem[] =>
  items.flatMap((item) => {
    if (item.permission && !hasPermission(item.permission.resource, item.permission.action)) {
      return [];
    }
    if (!item.children) return [item];
    const children = filterVisibleItems(item.children, hasPermission);
    return children.length > 0 ? [{ ...item, children }] : [];
  });

const BADGE_COMPONENTS: Record<
  SidebarBadgeKey,
  (props: { count: number; variant?: SidebarCountBadgeVariant }) => JSX.Element
> = {
  approvals: SidebarApprovalsBadge,
  approvalQueue: SidebarApprovalQueueBadge,
  analystQueue: SidebarAnalystQueueBadge,
  commissionAttention: SidebarCommissionAttentionBadge,
  competitorOfferQueue: SidebarCompetitorOfferBadge,
  endorsementQueue: SidebarEndorsementQueueBadge,
  paymentIncidentOpen: SidebarPaymentIncidentBadge,
  reactivationBillingOpen: SidebarReactivationBillingBadge,
  quotationExceptionQueue: SidebarQuotationExceptionQueueBadge,
  support: SidebarSupportBadge,
  vehicleQueue: SidebarVehicleQueueBadge,
};

interface SidebarItemBadgeProps {
  badgeKey: SidebarBadgeKey;
  counts: SidebarBadgeCounts;
}

/** Badge da folha: mostra a própria contagem, no visual destacado. */
function SidebarItemBadge({ badgeKey, counts }: SidebarItemBadgeProps) {
  const Badge = BADGE_COMPONENTS[badgeKey];
  return <Badge count={counts[badgeKey]} />;
}

const flattenFlyoutItems = (
  items: SubItem[],
  t: (key: string) => string,
  pathname: string,
): SidebarFlyoutItem[] =>
  items.flatMap((item) => {
    if (item.children?.length) {
      return flattenFlyoutItems(item.children, t, pathname).map((child) => ({
        ...child,
        label: `${t(item.labelKey)} · ${child.label}`,
      }));
    }
    return [
      {
        to: item.to,
        label: t(item.labelKey),
        icon: item.icon,
        isActive: isItemActive(item, pathname, items),
      },
    ];
  });

/**
 * Renders the administrative destinations as seven business-oriented groups.
 *
 * - On mount, every group starts collapsed except the one containing the active route
 * - Expansion state is session-only (in-memory): a reload collapses everything again
 * - SPA navigation auto-inflates the group of the new active route; groups the user
 *   opened manually stay open until reload
 * - Animation: transition-[max-height,opacity] duration-200 ease-out
 * - Permission-gated items are filtered via usePermissions().hasPermission
 */
export interface SidebarAdminSubGroupsProps {
  /** When true, renders only the group icons with a tooltip + first-item
   * link so the admin surfaces stay reachable while the sidebar is collapsed. */
  collapsed?: boolean;
}

/**
 * Returns true when the current role can see at least one admin sub-group.
 * Lets the layout hide the ADMINISTRACAO header/separator for roles (CLT,
 * consultor) whose permissions filter every group out.
 */
export function useHasVisibleAdminGroups(): boolean {
  const { hasPermission } = usePermissions();
  return useMemo(
    () =>
      GROUPS.some(
        (g) =>
          (!g.groupPermission ||
            hasPermission(g.groupPermission.resource, g.groupPermission.action)) &&
          filterVisibleItems(g.items, hasPermission).length > 0,
      ),
    [hasPermission],
  );
}

export function SidebarAdminSubGroups({ collapsed = false }: SidebarAdminSubGroupsProps) {
  // `competitor_offer` carregado por causa do item Alamo Cobre (ESCR-172.6).
  // `commissions` carregado por causa do grupo de comissões MLM.
  const { t } = useTranslation([
    'admin',
    'admin_pricing',
    'competitor_offer',
    'commissions',
    'support',
    'reports',
  ]);
  const { hasPermission } = usePermissions();
  const location = useLocation();
  const badgeCounts = useSidebarBadgeCounts();
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => {
    const activeKey = findActiveGroupKey(location.pathname);
    return activeKey ? { [activeKey]: true } : {};
  });

  // Filter groups + items by permission. Hide groups that have no visible items
  // or where the group-level gate (groupPermission) is not satisfied.
  const visibleGroups = useMemo(
    () =>
      GROUPS.filter(
        (g) =>
          !g.groupPermission || hasPermission(g.groupPermission.resource, g.groupPermission.action),
      )
        .map((g) => ({
          ...g,
          items: filterVisibleItems(g.items, hasPermission),
        }))
        .filter((g) => g.items.length > 0),
    [hasPermission],
  );

  const activeGroupKey = useMemo(
    () =>
      visibleGroups.find((g) => g.items.some((i) => isItemActive(i, location.pathname, g.items)))
        ?.key,
    [location.pathname, visibleGroups],
  );

  // Auto-inflate the group whose child is active. The adjustment runs during
  // render (not in an effect) so the group is already open in the very first
  // commit after a route change — an effect would paint one frame with the
  // group still collapsed, and the aggregate badge would blink there before
  // moving down to the leaf.
  const [lastActiveGroupKey, setLastActiveGroupKey] = useState(activeGroupKey);
  if (activeGroupKey !== lastActiveGroupKey) {
    setLastActiveGroupKey(activeGroupKey);
    if (activeGroupKey) {
      setExpanded((prev) => ({ ...prev, [activeGroupKey]: true }));
    }
  }

  const toggle = (key: string) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  if (visibleGroups.length === 0) return null;

  // Collapsed rail: render one NavLink per group pointing to its first item.
  // Hovering the icon opens a SidebarFlyout with the full sub-item list, so
  // users can reach every admin sub-route (Partes, Usuários, Consultores,
  // Multinível, Notificações, Ajustes, Exclusões) without re-expanding the
  // rail. Clicking the icon itself still navigates to the first item, which
  // keeps the click-based path unchanged for users who never hover.
  if (collapsed) {
    return (
      <nav className="space-y-1" aria-label="ADMINISTRACAO">
        {visibleGroups.map((g) => {
          const GroupIcon = g.icon;
          const firstItem = g.items[0];
          if (!firstItem) return null;
          const isActiveGroup = activeGroupKey === g.key;
          // Build flyout items with active state. Uses the same
          // `needsExactMatch` rule as the expanded mode so `/admin/network`
          // doesn't light up while we are on `/admin/network/consultants`.
          const flyoutItems = flattenFlyoutItems(g.items, t, location.pathname);
          // O rail é o ancestral colapsado mais alto possível: o ícone carrega a
          // soma de todas as pendências do grupo.
          const groupTotal = sumBadgeList(g.items, badgeCounts);
          return (
            <SidebarFlyout key={g.key} label={t(g.labelKey)} items={flyoutItems}>
              <NavLink
                to={firstItem.to}
                className={cn(
                  'relative flex items-center justify-center py-2 rounded-md transition-colors',
                  isActiveGroup
                    ? 'bg-primary-500/10 text-alamo-orange-400'
                    : 'text-white/70 hover:bg-white/5 hover:text-white',
                )}
                aria-label={t(g.labelKey)}
              >
                <GroupIcon className="size-5" aria-hidden />
                {groupTotal > 0 ? (
                  <span className="absolute right-0.5 top-0">
                    <SidebarAggregateBadge count={groupTotal} nodeKey={g.key} />
                  </span>
                ) : null}
              </NavLink>
            </SidebarFlyout>
          );
        })}
      </nav>
    );
  }

  return (
    <nav className="space-y-1" aria-label="ADMINISTRACAO">
      {visibleGroups.map((g) => {
        const isOpen = !!expanded[g.key];
        const GroupIcon = g.icon;
        const groupTotal = sumBadgeList(g.items, badgeCounts);
        return (
          <div key={g.key}>
            <button
              type="button"
              onClick={() => toggle(g.key)}
              aria-expanded={isOpen}
              aria-controls={`admin-group-${g.key}`}
              // `text-left` neutraliza o `text-align: center` que o browser dá a
              // <button>: sem ele o rótulo que quebra em duas linhas centraliza e
              // desalinha dos irmãos de uma linha só.
              className="w-full flex items-center justify-between pl-4 pr-3 py-2 text-sm text-left text-white/80 hover:bg-white/5 rounded-md"
            >
              <span className="flex min-w-0 items-center gap-2">
                <GroupIcon className="size-4 shrink-0" aria-hidden />
                <span className="min-w-0">{t(g.labelKey)}</span>
                {isOpen ? null : <SidebarAggregateBadge count={groupTotal} nodeKey={g.key} />}
              </span>
              <ChevronDown
                className={cn(
                  'size-4 shrink-0 transition-transform duration-200',
                  isOpen ? 'rotate-180' : '',
                )}
                aria-hidden
              />
            </button>
            <ul
              id={`admin-group-${g.key}`}
              className={cn(
                'list-none p-0 m-0 transition-[max-height,opacity] duration-200 ease-out overflow-hidden',
                isOpen ? 'max-h-[1400px] opacity-100' : 'max-h-0 opacity-0',
              )}
            >
              {g.items.map((it) => {
                if (it.children?.length) {
                  return (
                    <SidebarNestedSubmenu
                      key={it.to}
                      item={it}
                      pathname={location.pathname}
                      t={t}
                      counts={badgeCounts}
                      ancestorOpen={isOpen}
                    />
                  );
                }
                const ItemIcon = it.icon;
                // Enable end-match when another item in the same group has
                // a path that starts with this one plus a separator — e.g.
                // /admin/network must not light up while on
                // /admin/network/consultants.
                const needsExactMatch = g.items.some(
                  (other) => other.to !== it.to && other.to.startsWith(`${it.to}/`),
                );
                // Quando `activeWhen` está definido, ignora o cálculo do
                // NavLink e usa nossa lógica customizada (suporta rotas filhas
                // que não compartilham prefixo, ex.: /admin/propostas/:id/analise).
                const forcedActive = it.activeWhen
                  ? isItemActive(it, location.pathname, g.items)
                  : null;
                return (
                  <li key={it.to} className="list-none">
                    <NavLink
                      to={it.to}
                      end={needsExactMatch}
                      className={({ isActive }) => {
                        const active = forcedActive ?? isActive;
                        return cn(
                          'flex items-center gap-2 pl-8 pr-3 py-2 text-sm rounded-md',
                          active
                            ? 'bg-primary-500/10 border-l-[3px] border-primary-500 text-white font-bold'
                            : 'text-white/70 hover:bg-white/5',
                        );
                      }}
                    >
                      {ItemIcon ? <ItemIcon className="size-4 shrink-0" aria-hidden /> : null}
                      <span className="min-w-0 flex-1">{t(it.labelKey)}</span>
                      {isOpen && it.badgeKey ? (
                        <SidebarItemBadge badgeKey={it.badgeKey} counts={badgeCounts} />
                      ) : null}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </nav>
  );
}

interface SidebarNestedSubmenuProps {
  item: SubItem;
  pathname: string;
  t: (key: string) => string;
  counts: SidebarBadgeCounts;
  /** Estado do grupo que contém este submenu. Fechado, quem exibe o número é o grupo. */
  ancestorOpen: boolean;
}

function SidebarNestedSubmenu({
  item,
  pathname,
  t,
  counts,
  ancestorOpen,
}: SidebarNestedSubmenuProps) {
  const children = item.children ?? [];
  const isActive = isItemActive(item, pathname, [item]);
  const [open, setOpen] = useState(isActive);
  const nodeKey = item.to.replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '');
  const submenuId = `admin-submenu-${nodeKey}`;
  const ItemIcon = item.icon;
  const submenuTotal = sumBadges(item, counts);

  // Abre junto com a rota ativa. O ajuste roda em render (não em efeito) para
  // que o submenu já esteja aberto no primeiro commit — em efeito, o badge
  // agregado apareceria por um frame antes de descer para a folha.
  const [wasActive, setWasActive] = useState(isActive);
  if (isActive !== wasActive) {
    setWasActive(isActive);
    if (isActive) setOpen(true);
  }

  return (
    <li className="list-none">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-controls={submenuId}
        className={cn(
          // `text-left` neutraliza o `text-align: center` que o browser dá a
          // <button>, mantendo o rótulo à esquerda mesmo quando quebra linha.
          'w-full flex items-center gap-2 pl-8 pr-3 py-2 text-sm text-left rounded-md',
          isActive
            ? 'bg-primary-500/10 border-l-[3px] border-primary-500 text-white font-bold'
            : 'text-white/70 hover:bg-white/5',
        )}
      >
        {ItemIcon ? <ItemIcon className="size-4 shrink-0" aria-hidden /> : null}
        <span className="min-w-0 flex-1">{t(item.labelKey)}</span>
        {ancestorOpen && !open ? (
          <SidebarAggregateBadge count={submenuTotal} nodeKey={nodeKey} />
        ) : null}
        <ChevronDown
          className={cn(
            'size-4 shrink-0 transition-transform duration-200',
            open ? 'rotate-180' : '',
          )}
          aria-hidden
        />
      </button>
      <ul
        id={submenuId}
        className={cn(
          'list-none p-0 m-0 border-l border-white/10 ml-6 overflow-hidden transition-[max-height,opacity] duration-200 ease-out',
          open ? 'max-h-[900px] opacity-100' : 'max-h-0 opacity-0',
        )}
      >
        {children.map((child) => {
          const needsExactMatch = children.some(
            (sibling) => sibling.to !== child.to && sibling.to.startsWith(`${child.to}/`),
          );
          const childIsActive = isItemActive(child, pathname, children);
          const ChildIcon = child.icon;
          return (
            <li key={child.to} className="list-none">
              <NavLink
                to={child.to}
                end={needsExactMatch}
                className={cn(
                  'flex items-center gap-2 pl-5 pr-3 py-2 text-sm leading-5 rounded-md',
                  childIsActive
                    ? 'bg-primary-500/10 border-l-[3px] border-primary-500 text-white font-bold'
                    : 'text-white/65 hover:bg-white/5',
                )}
              >
                {ChildIcon ? <ChildIcon className="size-4 shrink-0" aria-hidden /> : null}
                <span className="min-w-0 flex-1">{t(child.labelKey)}</span>
                {ancestorOpen && open && child.badgeKey ? (
                  <SidebarItemBadge badgeKey={child.badgeKey} counts={counts} />
                ) : null}
              </NavLink>
            </li>
          );
        })}
      </ul>
    </li>
  );
}
